import { IntegrationsPortErrors } from 'modules/Integrations/domain'
import type { NetsuiteIntegrationConfig } from './netsuite.integration.config'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import omitBy from 'lodash/fp/omitBy'
import { persistAutoLinkCustomers } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/persistAutoLinkCustomers'

export const netsuiteConfigurationMutator = async (
  data: NetsuiteIntegrationConfig
): Promise<{
  success: boolean
  error: IntegrationsPortErrors | null
}> => {
  // A blank private key on a connected account means only the defaults changed,
  // so we update those and leave the stored credentials alone. Everything else
  // goes through the credentials endpoint, which is also the only way to
  // connect in the first place.
  const isSettingsOnlyUpdate = data.isConnected && !data.privateKey

  // An omitted item id clears the stored one, so a save carries every filled
  // id and leaves an emptied field out to clear it.
  const itemIds = omitBy(value => !value)({
    prepaidCreditItemId: data.prepaidCreditItemId,
    commitmentItemId: data.commitmentItemId,
    creditGrantItemId: data.creditGrantItemId,
    salesTaxItemId: data.salesTaxItemId,
    deferredRevenueItemId: data.deferredRevenueItemId
  })

  const [autoLinkSaved, result] = await Promise.all([
    persistAutoLinkCustomers('NetSuite', data.autoLinkCustomers),
    isSettingsOnlyUpdate
      ? dashboardv99990101Client.putNetsuiteSettings({
          subsidiaryId: data.subsidiaryId,
          defaultItemIncomeAccountId: data.defaultItemIncomeAccountId,
          defaultTaxScheduleId: data.defaultTaxScheduleId,
          ...itemIds
        })
      : dashboardv99990101Client.postStoreNetsuiteCredentials({
          netsuiteAccountId: data.netsuiteAccountId,
          subsidiaryId: data.subsidiaryId,
          consumerKey: data.consumerKey,
          certificateId: data.certificateId,
          defaultItemIncomeAccountId: data.defaultItemIncomeAccountId,
          defaultTaxScheduleId: data.defaultTaxScheduleId,
          ...itemIds,
          privateKey: data.privateKey
        })
  ])

  if (!autoLinkSaved || result.error) {
    return { success: false, error: IntegrationsPortErrors.Other }
  }

  return { success: true, error: null }
}
